"use client";

import * as React from "react";
import { LogOut, LayoutDashboard, Settings } from "lucide-react";
import { signOutAction } from "@/app/actions";
import { usePathname } from "next/navigation";
import Link from "next/link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ROLES, type RoleId } from "@/lib/constants/roles";
import { PORTAL_ROUTES } from "@/lib/routing/portal-routes";

interface User {
  id: string;
  email: string;
  role: string;
  role_id: RoleId;
  name?: string;
}

interface UserProfileDropdownProps {
  user: User;
}

export default function UserProfileDropdown({
  user,
}: UserProfileDropdownProps) {
  const pathname = usePathname();
  const [isSigningOut, startSignOut] = React.useTransition();

  // Check if we're in a venue-specific context
  const isInVenueContext = pathname.includes("/superadmin/venue/");
  const isAdmin =
    user.role_id === ROLES.SUPER_ADMIN || user.role_id === ROLES.BOOKING_STAFF;

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return "U";
  };

  const formatRole = (roleId: RoleId) => {
    switch (roleId) {
      case ROLES.SUPER_ADMIN:
        return "Super Admin";
      case ROLES.BOOKING_STAFF:
        return "Booking Staff";
      case ROLES.BAR_STAFF:
        return "Bar Staff";
      case ROLES.READONLY:
        return "Readonly";
      default:
        return "Unknown Role";
    }
  };

  const initials = getInitials(user.name, user.email);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-8 w-8 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          suppressHydrationWarning
        >
          <span className="text-sm font-medium text-foreground">
            {initials}
          </span>
          <span className="sr-only">Open user menu</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {user.name || user.email}
            </p>
            {user.name && (
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            )}
            <p className="text-xs leading-none text-muted-foreground">
              {formatRole(user.role_id)}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isInVenueContext && isAdmin && (
          <>
            <DropdownMenuItem asChild>
              <Link href="/superadmin/dashboard" className="cursor-pointer">
                <LayoutDashboard className="mr-2 h-4 w-4" />
                <span>Back to admin dashboard</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {user.role_id === ROLES.SUPER_ADMIN && (
          <>
            <DropdownMenuItem asChild>
              <Link
                href={PORTAL_ROUTES.superadmin.settings}
                className="cursor-pointer"
              >
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {/* Call the action from onSelect rather than wrapping a <form>: the
            menu closes on select and unmounted the form before it submitted,
            so sign out sometimes took several clicks. */}
        <DropdownMenuItem
          className="cursor-pointer text-red-600 focus:text-red-600"
          disabled={isSigningOut}
          onSelect={(event) => {
            // Keep the menu open so "Signing out…" shows until the redirect.
            event.preventDefault();
            startSignOut(() => signOutAction());
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>{isSigningOut ? "Signing out…" : "Sign out"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
